import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Invoice } from '../types/invoice.type';
import { InvoiceSetting } from '../types/invoice-setting.type';
import LogoPng from '../assets/Logo.png';
import { reportExport } from './auditExport';

const DEFAULT_CLUB_NAME = 'UFC SV Hallwang';

const PURPLE = [91, 44, 111] as [number, number, number];
const GRAY = [100, 100, 100] as [number, number, number];
const RED = [200, 0, 0] as [number, number, number];

function fmtEur(value: number): string {
  return `€ ${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateDE(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// jsPDF embeds images at their native pixel resolution regardless of the
// display size passed to addImage(), so a large source image (e.g. a
// 966x1320 RGBA logo) bloats the PDF far beyond what's needed for a
// 35mm-tall print. Downscale to a print-appropriate resolution first.
async function loadScaledImage(src: string, displayHeightMm: number): Promise<HTMLCanvasElement> {
  const img = await loadImage(src);
  const targetDpi = 300;
  const targetHeightPx = Math.round((displayHeightMm / 25.4) * targetDpi);
  const scale = Math.min(1, targetHeightPx / img.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

const DEFAULT_SENDER_LINE = 'Sportplatzstraße 1 | 5300 Hallwang | Austria | office@sv-hallwang.at | www.sv-hallwang.at';
const DEFAULT_FOOTER_LEFT = 'KM IBAN: AT90 3502 3000 0001 1478\nNW IBAN: AT92 3501 5000 1801 8077';
const DEFAULT_FOOTER_CENTER = 'SFV: 5036\nZVR: 026368260';
const DEFAULT_FOOTER_RIGHT = 'office@sv-hallwang.at\nwww.sv-hallwang.at';

export async function generateInvoicePdf(invoice: Invoice, settings?: InvoiceSetting): Promise<void> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 20;
  const isStorno = invoice.isCancellationDocument ?? false;

  const senderLine = settings?.senderAddressLine ?? DEFAULT_SENDER_LINE;
  const footerLeft = settings?.footerLeft ?? DEFAULT_FOOTER_LEFT;
  const footerCenter = settings?.footerCenter ?? DEFAULT_FOOTER_CENTER;
  const footerRight = settings?.footerRight ?? DEFAULT_FOOTER_RIGHT;

  // Logo centered at top — prefer dynamic logo from settings, fall back to static asset
  try {
    const logoSrc = settings?.logoBase64 ?? LogoPng;
    const logoH = 35;
    const logoCanvas = await loadScaledImage(logoSrc, logoH);
    const logoW = logoH * (logoCanvas.width / logoCanvas.height);
    const logoX = (pageW - logoW) / 2;
    doc.addImage(logoCanvas, 'PNG', logoX, 8, logoW, logoH);
  } catch {
    // Logo unavailable — skip silently
  }

  // Address line in purple below logo
  doc.setFontSize(8);
  doc.setTextColor(...PURPLE);
  doc.text(senderLine, pageW / 2, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // Recipient block (left)
  let y = 65;
  doc.setFontSize(10);
  if (invoice.recipientLine1) {
    doc.text(invoice.recipientLine1, margin, y);
    y += 5;
  }
  if (invoice.recipientName) {
    doc.setFont('helvetica', 'bold');
    doc.text(invoice.recipientName, margin, y);
    doc.setFont('helvetica', 'normal');
    y += 5;
  }
  if (invoice.recipientStreet) {
    doc.text(invoice.recipientStreet, margin, y);
    y += 5;
  }
  if (invoice.recipientCity) {
    doc.text(invoice.recipientCity, margin, y);
  }

  // Date (right side)
  const dateStr = `Hallwang, ${formatDateDE(invoice.invoiceDate)}`;
  doc.setFontSize(10);
  doc.text(dateStr, pageW - margin, 73, { align: 'right' });

  // Title — cancellation invoices get a prominent red header
  const titleY = 98;
  if (isStorno) {
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...RED);
    doc.text('STORNO', margin, titleY);
    doc.setFontSize(12);
    doc.setTextColor(...PURPLE);
    doc.text(`Rechnung ${invoice.invoiceNumber ?? ''}`, margin, titleY + 9);
  } else {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PURPLE);
    doc.text(invoice.invoiceNumber ? `Rechnung ${invoice.invoiceNumber}` : 'Rechnung (Entwurf)', margin, titleY);
  }
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  if (invoice.leistungszeitraum) {
    doc.setFontSize(10);
    doc.text(`Leistungszeitraum: ${invoice.leistungszeitraum}`, pageW - margin, titleY, { align: 'right' });
  }

  // Positions table
  const positions = (invoice.positions ?? []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const tableBody = positions.map(p => [
    String(Number(p.quantity) % 1 === 0 ? Number(p.quantity) : Number(p.quantity).toFixed(2)),
    p.description,
    fmtEur(Number(p.unitPrice)),
    fmtEur(Number(p.quantity) * Number(p.unitPrice)),
  ]);

  tableBody.push(['', '', '', '']);

  const subtotal = positions.reduce((sum, p) => sum + Number(p.quantity) * Number(p.unitPrice), 0);
  const vatAmount = subtotal * (Number(invoice.vatRate) / 100);
  const total = subtotal + vatAmount;

  autoTable(doc, {
    startY: titleY + (isStorno ? 14 : 5),
    head: [['Menge', 'Beschreibung', 'Einzelpreis', 'Summe']],
    body: tableBody,
    columnStyles: {
      0: { cellWidth: 20, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
      3: { cellWidth: 35, halign: 'right' },
    },
    headStyles: { fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [0, 0, 0], lineWidth: 0.2, lineColor: [180, 180, 180] },
    bodyStyles: { lineWidth: 0.2, lineColor: [180, 180, 180] },
    theme: 'plain',
    foot: [
      ['', '', 'Zwischensumme', fmtEur(subtotal)],
      ['', '', `Umsatzsteuer (${Number(invoice.vatRate)}%)`, fmtEur(vatAmount)],
      ['', '', 'Zahlbetrag', fmtEur(total)],
    ],
    footStyles: { fontStyle: 'normal', lineWidth: 0.2, lineColor: [180, 180, 180] },
    didParseCell: (data) => {
      if (data.section === 'foot') {
        if (data.column.index >= 2) data.cell.styles.halign = 'right';
        if (data.row.index === 2) data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  const finalY: number = (doc as any).lastAutoTable.finalY ?? 160;

  // Post-text
  if (invoice.postText) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(invoice.postText, pageW - margin * 2);
    doc.text(lines, margin, finalY + 12);
  }

  const closingY = finalY + (invoice.postText ? 35 : 20);

  // Closing
  doc.setFontSize(10);
  doc.text('Freundliche Grüße', margin, closingY);
  doc.setTextColor(...PURPLE);
  doc.text(settings?.clubName ?? DEFAULT_CLUB_NAME, margin, closingY + 6);
  doc.setTextColor(0, 0, 0);

  if (invoice.vatText) {
    doc.setFontSize(7);
    doc.text(invoice.vatText, margin, closingY + 24);
  }

  // Footer bar
  const footerY = 272;
  const footerLineHeight = 4;
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');

  footerLeft.split('\n').forEach((line, i) => {
    doc.text(line, margin, footerY + i * footerLineHeight);
  });

  const col2 = pageW / 2;
  const centerLines = footerCenter.split('\n');
  centerLines.forEach((line, i) => {
    doc.text(line, col2, footerY + i * footerLineHeight, { align: 'center' });
  });

  const col3 = pageW - margin;
  footerRight.split('\n').forEach((line, i) => {
    doc.text(line, col3, footerY + i * footerLineHeight, { align: 'right' });
  });

  doc.setTextColor(0, 0, 0);

  // Punching-hole margin text (DIN 824: holes at 80mm and 217mm from top)
  doc.setFontSize(5);
  doc.setTextColor(160, 160, 160);
  doc.text('das Runde', 3, 80);
  doc.text('muss', 3, 148.5, { align: 'center' });
  doc.text('ins Eckige', 3, 217);
  doc.setTextColor(0, 0, 0);

  // Diagonal DRAFT watermark for non-fixed invoices
  if (!invoice.isFixed) {
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFontSize(80);
    doc.setTextColor(210, 210, 210);
    doc.text('DRAFT', pageW / 2, pageH / 2, { align: 'center', angle: 45 });
    doc.setTextColor(0, 0, 0);
  }

  doc.save(`Rechnung_${invoice.invoiceNumber ?? 'Entwurf'}.pdf`);
  reportExport('invoice-pdf', {
    details: { invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber ?? null, recipient: invoice.recipientName ?? null },
  });
}
